import React, { FC, useMemo } from 'react';
import { Stack } from '@mui/material';
import NormalSignupOrderSummary from 'src/pages/payment-new/components/normal-signup-order-summary/NormalSignupOrderSummary';
import { EffectivePlanObjType } from 'src/pages/payment-new/types';
import { getFullPlanObj } from 'src/lib/utils';
import { useAppSelector } from 'src/store/hooks';
import { ReactivationSubscriptionDetails } from 'src/services/reactivation.service';
import { ReactivationCampaignType } from 'src/services/reactivation.types';
import {
  getDiscountRolloverAmounts,
  getReactivationOrderDescription,
  resolveOrderSummaryPlanId,
} from '../utils/orderSummaryUtils';

interface ReactivateOrderSummaryProps {
  subscription?: ReactivationSubscriptionDetails | null;
  onChangePlan: () => void;
  badgeLabel?: string;
  showFreeTrial?: boolean;
  campaignType?: ReactivationCampaignType | null;
}

export const ReactivateOrderSummary: FC<ReactivateOrderSummaryProps> = ({
  subscription,
  onChangePlan,
  badgeLabel,
  showFreeTrial = false,
  campaignType,
}) => {
  const { selectedPlanId, allPlansSorted, addonWeek } = useAppSelector(
    state => state.signUp
  );

  const resolvedPlanId = useMemo(
    () =>
      resolveOrderSummaryPlanId(selectedPlanId, subscription, allPlansSorted),
    [selectedPlanId, subscription, allPlansSorted]
  );

  const effectivePlanObj = useMemo((): EffectivePlanObjType | null => {
    if (resolvedPlanId) {
      return getFullPlanObj(allPlansSorted, resolvedPlanId, addonWeek) ?? null;
    }
    return null;
  }, [allPlansSorted, resolvedPlanId, addonWeek]);

  const isDiscountRollover =
    campaignType === ReactivationCampaignType.DISCOUNT_ROLLOVER;

  const { originalBillingAmount, billingAmountOverride } = useMemo(
    () =>
      getDiscountRolloverAmounts(
        isDiscountRollover,
        effectivePlanObj,
        subscription,
        resolvedPlanId
      ),
    [isDiscountRollover, effectivePlanObj, subscription, resolvedPlanId]
  );

  const description = useMemo(
    () =>
      getReactivationOrderDescription({
        showFreeTrial,
        campaignType,
        effectivePlanObj,
        discountedAmount: billingAmountOverride,
      }),
    [showFreeTrial, campaignType, effectivePlanObj, billingAmountOverride]
  );

  if (!effectivePlanObj) {
    return null;
  }

  return (
    <Stack width="100%" maxWidth="400px" gap="16px" margin="0 auto">
      <NormalSignupOrderSummary
        effectivePlanObj={effectivePlanObj}
        handleChangePlan={onChangePlan}
        onRemoveNutritionCoaching={() => {}}
        onRemoveSupplement={() => {}}
        description={description}
        originalBillingAmount={originalBillingAmount}
        billingAmountOverride={billingAmountOverride}
        showFreeTrial={showFreeTrial}
        badgeLabel={badgeLabel}
        useOutlineButton
      />
    </Stack>
  );
};
